import ThemeModal from "components/Common/ThemeModal"
import { Badge, Col, Row, Spinner } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { getEntityLabel, getUserLabel } from "pages/AccountPages/utils/displayValue"

const getMoneyDisplayValue = (value, currency = "BDT") => formatCurrency(value || 0, currency)

const ReceiptRow = ({ label, value, mono = false, strong = false }) => {
  return (
    <div className="d-flex align-items-start justify-content-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}>
      <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
      <span className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`.trim()} style={{ fontSize: strong ? 15 : 14 }}>
        {value || "-"}
      </span>
    </div>
  )
}

const ReceiptSection = ({ title, children }) => {
  return (
    <div className="pt-3">
      <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>{title}</div>
      {children}
    </div>
  )
}

const AccountVendorPaymentView = ({ modal, toggleModal, payment, loading }) => {
  const vendorBill = payment?.vendor_bill || payment?.vendorBill
  const vendorName = getEntityLabel(vendorBill?.vendor, vendorBill?.vendor_id, "Vendor")
  const billNo = getEntityLabel(vendorBill, payment?.vendor_bill_id, "Bill")
  const accountName = getEntityLabel(payment?.account, payment?.account_id, "Account")
  const paymentMethodName = getEntityLabel(payment?.payment_method || payment?.paymentMethod, payment?.payment_method_id, "Method")
  const createdBy = getUserLabel(payment?.createdBy || payment?.created_by)
  const updatedBy = getUserLabel(payment?.updatedBy || payment?.updated_by)

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Vendor Payment Details" size="xl" modalBodyClass="bg-body-tertiary">
      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : !payment ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 740 }}>
          {payment.is_reversed && (
            <div className="alert alert-warning d-flex align-items-center gap-2 mb-3 py-2 px-3 rounded-3" style={{ fontSize: 13 }}>
              <i className="bx bx-info-circle fs-5" />
              <span>This payment has been <strong>reversed</strong>. It has been cancelled by a ledger reversal entry and no longer counts as an expense.</span>
            </div>
          )}
          <div className="rounded-4 border overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)", borderColor: "rgba(15, 23, 42, 0.08)", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)" }}>
            <div className="px-4 py-2 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)" }}>
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>Vendor Payment Receipt</div>
              <div className="text-white-50 font-monospace" style={{ fontSize: 12 }}>AVP-{String(payment.id ?? "0000").padStart(4, "0")}</div>
            </div>
            <div className="px-4 py-3 border-bottom" style={{ background: "radial-gradient(circle at top left, rgba(8, 145, 178, 0.14), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)", borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Vendor payment</div>
                  <div className="h5 mb-1 text-dark" style={{ letterSpacing: -0.2 }}>{vendorName}</div>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge color="danger" pill className="px-2 py-1">Settled</Badge>
                    {payment.is_reversed && <Badge color="secondary" pill className="px-2 py-1">Reversed</Badge>}
                    <span className="text-muted small font-monospace">#{payment.id ?? "-"}</span>
                    <span className="text-muted small">{payment.payment_date ? formatReadableDate(payment.payment_date) : "No date"}</span>
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Amount</div>
                  <div className="fw-bold text-dark" style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}>{getMoneyDisplayValue(payment.amount)}</div>
                  <div className="text-muted small mt-1">via {paymentMethodName}</div>
                </div>
              </div>
            </div>
            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed rgba(15, 23, 42, 0.18)" }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, left: -11, top: -2 }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, right: -11, top: -2 }} />
            </div>
            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Receipt Details">
                    <ReceiptRow label="Reference" value={payment.reference_number || "Not provided"} mono />
                    <ReceiptRow label="Bill Number" value={billNo} mono />
                    <ReceiptRow label="Account" value={accountName} />
                    <ReceiptRow label="Payment Date" value={payment.payment_date ? formatReadableDate(payment.payment_date) : "-"} />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Related Entities">
                    <ReceiptRow label="Vendor" value={vendorName} />
                    <ReceiptRow label="Method" value={paymentMethodName} />
                    <ReceiptRow label="Record ID" value={payment.id ?? "-"} mono />
                    <ReceiptRow label="Vendor Bill" value={billNo} mono />
                  </ReceiptSection>
                </Col>
              </Row>
            </div>
            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Payment Ledger">
                <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  <ReceiptRow label="Description" value="Vendor bill payment settled" />
                  <ReceiptRow label="Channel" value={paymentMethodName} />
                  <ReceiptRow label="Allocation Account" value={accountName} />
                  <ReceiptRow label="Total Paid" value={getMoneyDisplayValue(payment.amount)} strong mono />
                  <ReceiptRow label="Ledger Status" value={payment.is_reversed ? "⚠ Reversed — Cancelled by reversal entry" : "Active"} />
                </div>
              </ReceiptSection>
            </div>
            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Notes">
                <div className="text-dark rounded-3 px-3 py-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, background: "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  {payment.notes || "No notes added for this payment."}
                </div>
              </ReceiptSection>
            </div>
            <div className="px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)" }}>
              <Row className="g-3">
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Created</div>
                  <div className="small text-dark">{createdBy}</div>
                  <div className="small text-muted">{payment.created_at ? formatReadableDate(payment.created_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Updated</div>
                  <div className="small text-dark">{updatedBy}</div>
                  <div className="small text-muted">{payment.updated_at ? formatReadableDate(payment.updated_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Status</div>
                  <div className="small text-dark fw-semibold">{payment.deleted_at ? "Archived" : "Active"}</div>
                  <div className="small text-muted">{payment.deleted_at ? formatReadableDate(payment.deleted_at) : "No deletion recorded"}</div>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountVendorPaymentView